import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import {
	CatalogPartEditApi,
	CatalogPartEditApi_FormInitData,
} from "src/api/generated/erp/parts/catalogPart/api/catalogPartEditApi.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { DeepPartial } from "react-hook-form";
import {
	CatalogPartFormContent,
	CatalogPartFormValues,
} from "src/components/views/erp/parts/catalogPart/CatalogPartFormContent.tsx";
import { CatalogPartUpdateService_UpdateRequest } from "src/api/generated/erp/parts/catalogPart/service/catalogPartUpdateService.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";

function mapFormValuesToUpdateRequest(
	existingCatalogPart: CatalogPart,
	formValues: CatalogPartFormValues,
): CatalogPartUpdateService_UpdateRequest {
	return {
		catalogPartId: existingCatalogPart.catalogPartId,
		lastUpdated: existingCatalogPart.lastUpdated,
		description1: formValues.description_1,
		description2: formValues.description_2,
		manufacturerPartNumber: formValues.manufacturerPartNumber,
		additionalNumber: formValues.additionalNumber,
		categoryId: formValues.categoryId,
		responsiblePersonId: formValues.responsiblePersonId,
		designUnitId: formValues.designUnitId,
		designUnitFactor: formValues.designUnitFactor,
		weight: formValues.weight,
		isConfigurable: formValues.isConfigurable,
		configurationProductFamilyId: formValues.isConfigurable ? formValues.configurationProductFamilyId : null,
	};
}

export interface CatalogPartFormProps extends FormCommonProps<number> {
	catalogPartId: number | null;
	partCategoryId: number | null;
}

export const CatalogPartForm = ({ catalogPartId, partCategoryId, onFormEdited, onCompleted }: CatalogPartFormProps) => {
	const { appUserId, defaultSiteId } = useGlobalInitData();
	const [storedState, setStoredState] = useStoredState<DeepPartial<CatalogPartFormValues>>({
		defaultValue: {
			partNumberInsertType: "numberSequenceId",
			numberSequenceId: undefined,
		},
		key: "9A6D7E8DEB369ACC",
	});
	return (
		<AsyncForm<CatalogPartEditApi_FormInitData, CatalogPartFormValues, number>
			fetch={() => CatalogPartEditApi.getFormInitData({ catalogPartId: catalogPartId })}
			columns={2}
			getDefaultValues={({ catalogPart }) =>
				catalogPart != null ?
					{ ...catalogPart }
				:	{
						categoryId: partCategoryId ?? undefined,
						responsiblePersonId: appUserId,
						designUnitFactor: 1,
						weight: 0,
						isConfigurable: false,
						partNumberInsertType: storedState.partNumberInsertType,
						numberSequenceId: storedState.numberSequenceId,
						copyPartToSiteParams: {
							partType: "PURCHASE",
							acquisitionMethod: "WAREHOUSE",
							warehouseTransferFactor: 1,
							catalogPartId: -1,
							siteId: defaultSiteId,
							valuationMethod: "STANDARD_COST",
							responsiblePersonId: appUserId,
						},
					}
			}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.catalogPart.form"),
				data: {
					catalogPartId: catalogPartId,
					partCategoryId: partCategoryId,
				},
			}}
			render={({ data, ...formContentParams }) => (
				<CatalogPartFormContent isNewRecord={catalogPartId == null} initData={data} {...formContentParams} />
			)}
			submit={async (formValues, { data: { catalogPart: existingCatalogPart } }) => {
				if (existingCatalogPart == null) {
					setStoredState({
						partNumberInsertType: formValues.partNumberInsertType,
						numberSequenceId: formValues.numberSequenceId,
					});
					return await CatalogPartEditApi.insert({
						catalogPart: formValues as any, // CatalogPartFormValues is compatible with CatalogPart for insert
						partNoSequenceId: undefined,
						catalogPartNumberDefiner:
							formValues.partNumberInsertType === "manuallyInsertedPartNumber" ?
								{
									type: "partNumber",
									partNo: formValues.manuallyInsertedPartNumber,
								}
							:	{
									type: "numberSequenceId",
									numberSequenceId: formValues.numberSequenceId!,
								},
					});
				} else {
					return await CatalogPartEditApi.update({
						request: mapFormValuesToUpdateRequest(existingCatalogPart, formValues),
					});
				}
			}}
		/>
	);
};
